import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core'
import { useCallback, useEffect, useRef } from 'react'

import {
  getDropPointFromDragEvent,
  getPointerCoordinates,
  type PointerCoordinates,
} from './tripDragReorder'

interface UseDragPointerTrackingParams {
  /** True between drag start and drag end/cancel; gates the window listeners. */
  isDragActive: boolean
  /** Called with a fresh drop point whenever the pointer moves or a surface scrolls. */
  onDropPointChange: (dropPoint: PointerCoordinates) => void
}

interface UseDragPointerTrackingResult {
  /** Capture the pickup point and the grab offset from the tile's centre. */
  startTracking: (event: DragStartEvent) => void
  /** Clear the tracked pointer state once the drag settles or is abandoned. */
  stopTracking: () => void
  /** The viewport point the drop should resolve against for this drag event. */
  getDropPoint: (event: DragMoveEvent | DragEndEvent) => PointerCoordinates | undefined
}

/**
 * Pointer bookkeeping shared by every trip planner drag surface (timeline canvas,
 * desktop list, mobile list). Each surface owns its own drop-target resolution and
 * optimistic reordering, but they all need the same answer to "which viewport point
 * is this drag currently over?", and getting it wrong shows up as an insertion line
 * that lags the drag overlay.
 */
export function useDragPointerTracking({
  isDragActive,
  onDropPointChange,
}: UseDragPointerTrackingParams): UseDragPointerTrackingResult {
  const dragOriginRef = useRef<PointerCoordinates | undefined>(undefined)
  const dragCenterOffsetRef = useRef<PointerCoordinates | undefined>(undefined)
  const pointerPositionRef = useRef<PointerCoordinates | undefined>(undefined)

  const startTracking = useCallback((event: DragStartEvent) => {
    const origin = getPointerCoordinates(event.activatorEvent)
    dragOriginRef.current = origin
    dragCenterOffsetRef.current = undefined
    // Leave the live pointer position unset until a real pointer/touch event fires.
    // Seeding it with the drag origin would make a fast drag→drop resolve from the
    // pickup point (getPointerDropPoint short-circuits the translated-rect fallback)
    // if the move/end handler runs before the first pointer event.
    pointerPositionRef.current = undefined

    const initialRect = event.active.rect.current.initial
    if (initialRect && origin) {
      dragCenterOffsetRef.current = {
        x: initialRect.left + initialRect.width / 2 - origin.x,
        y: initialRect.top + initialRect.height / 2 - origin.y,
      }
    }
  }, [])

  const stopTracking = useCallback(() => {
    dragOriginRef.current = undefined
    dragCenterOffsetRef.current = undefined
    pointerPositionRef.current = undefined
  }, [])

  // Resolve the drop point from the live pointer position rather than dnd-kit's
  // translated rect / delta, which accumulate the scroll offset of the nested
  // scroll container and drift out of sync with the (viewport-fixed) drag overlay.
  const getPointerDropPoint = useCallback((): PointerCoordinates | undefined => {
    const pointer = pointerPositionRef.current
    if (!pointer) return undefined

    const centerOffset = dragCenterOffsetRef.current
    return {
      x: pointer.x + (centerOffset?.x ?? 0),
      y: pointer.y + (centerOffset?.y ?? 0),
    }
  }, [])

  const getDropPoint = useCallback((event: DragMoveEvent | DragEndEvent): PointerCoordinates | undefined => {
    return getPointerDropPoint() ?? getDropPointFromDragEvent(event, dragOriginRef.current, dragCenterOffsetRef.current)
  }, [getPointerDropPoint])

  // While dragging, keep the drop target aligned with the overlay by tracking the
  // real pointer position and recomputing whenever the surface scrolls. The pointer
  // stays put during a scroll, so the tiles underneath it change and the insertion
  // line must follow them instead of relying on a stale, scroll-polluted delta.
  useEffect(() => {
    if (!isDragActive) return

    // Resolving a drop target hit-tests with `document.elementsFromPoint`, which forces
    // a synchronous layout. Pointer and scroll events fire far faster than the screen
    // refreshes, so recomputing on each one burns a layout per event and visibly janks
    // the drag. Coalesce to at most one hit-test per frame instead.
    //
    // Only the indicator is throttled, never the drop itself: `getDropPoint` reads the
    // pointer ref directly and the drop handlers re-resolve the target from it, so a
    // drop still lands exactly where the finger was even if a frame was skipped.
    let scheduledFrame = 0

    const recompute = () => {
      scheduledFrame = 0
      const dropPoint = getPointerDropPoint()
      if (dropPoint) onDropPointChange(dropPoint)
    }

    const scheduleRecompute = () => {
      if (scheduledFrame) return
      scheduledFrame = window.requestAnimationFrame(recompute)
    }

    const handlePointerMove = (event: PointerEvent | TouchEvent) => {
      const coordinates = getPointerCoordinates(event)
      if (coordinates) pointerPositionRef.current = coordinates
      scheduleRecompute()
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('touchmove', handlePointerMove, { passive: true })
    // Scroll events don't bubble; capture-phase catches the nested scroll container.
    window.addEventListener('scroll', scheduleRecompute, true)

    return () => {
      if (scheduledFrame) window.cancelAnimationFrame(scheduledFrame)
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('touchmove', handlePointerMove)
      window.removeEventListener('scroll', scheduleRecompute, true)
    }
  }, [getPointerDropPoint, isDragActive, onDropPointChange])

  return { startTracking, stopTracking, getDropPoint }
}
